/**
 * UserReportStats — shows the current user's submission statistics.
 * data-testid: candeza-reports-stats-*
 */

import React from 'react';
import styled from 'styled-components';
import { useMyStats } from './api';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`;

const KpiCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  text-align: center;
`;

const KpiValue = styled.div`
  font-size: 28px;
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const KpiLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;
`;

const TypeBreakdown = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const TypeBadge = styled.span`
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 12px;
  padding: 3px 10px;
  font-size: 12px;
`;

export const UserReportStats: React.FC = () => {
  const { data, isLoading } = useMyStats();

  if (isLoading) {
    return <div data-testid="candeza-reports-stats-loading">Loading stats…</div>;
  }

  if (!data) return null;

  return (
    <div data-testid="candeza-reports-stats-panel">
      <Grid>
        <KpiCard data-testid="candeza-reports-stats-total">
          <KpiValue>{data.total_submitted}</KpiValue>
          <KpiLabel>Total Submitted</KpiLabel>
        </KpiCard>
        <KpiCard data-testid="candeza-reports-stats-week">
          <KpiValue>{data.this_week}</KpiValue>
          <KpiLabel>This Week</KpiLabel>
        </KpiCard>
        <KpiCard data-testid="candeza-reports-stats-month">
          <KpiValue>{data.this_month}</KpiValue>
          <KpiLabel>This Month</KpiLabel>
        </KpiCard>
        <KpiCard data-testid="candeza-reports-stats-quarter">
          <KpiValue>{data.this_quarter}</KpiValue>
          <KpiLabel>This Quarter</KpiLabel>
        </KpiCard>
      </Grid>

      {Object.keys(data.by_type).length > 0 && (
        <TypeBreakdown data-testid="candeza-reports-stats-by-type">
          {Object.entries(data.by_type).map(([type, count]) => (
            <TypeBadge key={type} data-testid={`candeza-reports-stats-type-${type}`}>
              {type}: {count}
            </TypeBadge>
          ))}
        </TypeBreakdown>
      )}

      {data.last_report && (
        <div
          style={{ fontSize: 12, color: '#64748b', marginTop: 12 }}
          data-testid="candeza-reports-stats-last-report"
        >
          Last submitted: {new Date(data.last_report).toLocaleDateString()}
        </div>
      )}
    </div>
  );
};

